import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Save, Send, RefreshCw, CheckCircle, Download, User, Phone, Mail, Loader2, PenLine } from 'lucide-react';
import { API_URL, STATUS_COLORS, STATUS_LABELS } from '../components/booking/bookingUtils';
import { FillTypeLegend } from '../components/booking/FillTypeBadge';
import { OfficeSectionCard } from '../components/booking/OfficeSectionCard';
import { ClientLinkPanel, DocumentsPanel, DeclarationPanel, TimelinePanel } from '../components/booking/BookingPanels';
import { SendLinkDialog } from '../components/booking/SendLinkDialog';
import { CompanySignDialog } from '../components/booking/CompanySignDialog';
import { downloadBookingPdf } from '../utils/bookingPdf';

export default function OfficeBookingFormPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [officeValues, setOfficeValues] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendLinkOpen, setSendLinkOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const fetchBooking = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/booking-forms/bookings/${bookingId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Booking not found');
      const data = await res.json();
      setBooking(data);
      setOfficeValues(data.office_responses || {});
      setDirty(false);
    } catch (e) {
      toast.error(e.message);
      navigate('/booking-forms');
    } finally {
      setLoading(false);
    }
  }, [bookingId, navigate]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const onFieldChange = (fieldId, value) => {
    setOfficeValues(prev => ({ ...prev, [fieldId]: value }));
    setDirty(true);
  };

  const patchBooking = async (body, successMsg) => {
    const res = await fetch(`${API_URL}/api/booking-forms/bookings/${bookingId}`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.json()).detail || 'Update failed');
    toast.success(successMsg);
    await fetchBooking();
  };

  const saveOffice = async () => {
    setSaving(true);
    try { await patchBooking({ office_responses: officeValues }, 'Office fields saved'); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const updateStatus = (status) => patchBooking({ status }, `Status updated to ${STATUS_LABELS[status]}`).catch(e => toast.error(e.message));

  const requestResubmission = async () => {
    const res = await fetch(`${API_URL}/api/booking-forms/bookings/${bookingId}/request-resubmission`, { method: 'POST', credentials: 'include' });
    if (!res.ok) return toast.error('Failed to request resubmission');
    toast.success('Resubmission requested');
    fetchBooking();
  };

  const emailSignedCopy = async () => {
    setEmailing(true);
    try {
      const res = await fetch(`${API_URL}/api/booking-forms/bookings/${bookingId}/email-signed-copy`, { method: 'POST', credentials: 'include' });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to send email');
      const data = await res.json();
      toast.success(`Signed copy emailed to ${data.sent_to}`);
      fetchBooking();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setEmailing(false);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }
  if (!booking) return null;

  const schema = booking.template_version?.schema_json || {};
  const sections = schema.sections || [];
  const customer = booking.customer || {};
  const status = booking.status;

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="office-booking-form-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <header className="bg-[#0F172A] text-white rounded-xl p-6 sm:p-8 shadow-xl border border-slate-800 relative overflow-hidden">
          <div className="absolute inset-x-0 bottom-0 h-1 bg-[#D4AF37]" />
          <button onClick={() => navigate('/booking-forms')} className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors mb-4" data-testid="back-to-bookings-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Booking Forms
          </button>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="space-y-3 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Official Booking Form · Office View</p>
              <h1 className="font-heading text-2xl sm:text-3xl leading-tight" data-testid="office-form-title">{booking.template_name}</h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-white/80">
                <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4 text-[#D4AF37]" />{customer.full_name || 'Unknown customer'}</span>
                {customer.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4 text-[#D4AF37]" />{customer.phone}</span>}
                {customer.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4 text-[#D4AF37]" />{customer.email}</span>}
              </div>
              <p className="font-mono text-xs text-white/50">Ref {booking.booking_id}</p>
            </div>
            <div className="flex flex-col items-start lg:items-end gap-3 shrink-0">
              <Badge className={`${STATUS_COLORS[status]} text-xs px-3 py-1`} data-testid="office-form-status">{STATUS_LABELS[status]}</Badge>
              <div className="flex flex-wrap gap-2">
                <Button onClick={saveOffice} disabled={!dirty || saving} className="bg-[#D4AF37] text-[#0F172A] hover:bg-[#B5952F] hover:text-white font-bold" data-testid="save-office-fields-btn">
                  <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Office Fields'}
                </Button>
                {status === 'draft' && (
                  <Button variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/15 hover:text-white" onClick={() => setSendLinkOpen(true)} data-testid="send-link-btn">
                    <Send className="w-4 h-4 mr-2" /> Send Link
                  </Button>
                )}
                {booking.public_url && !['submitted', 'verified', 'confirmed'].includes(status) && (
                  <Button variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/15 hover:text-white" onClick={() => setSendLinkOpen(true)} data-testid="resend-link-btn">
                    <RefreshCw className="w-4 h-4 mr-2" /> Resend Link
                  </Button>
                )}
                {status === 'submitted' && (
                  <>
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => updateStatus('verified')} data-testid="mark-verified-btn">
                      <CheckCircle className="w-4 h-4 mr-2" /> Mark Verified
                    </Button>
                    <Button variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/15 hover:text-white" onClick={requestResubmission} data-testid="request-resubmission-btn">
                      <RefreshCw className="w-4 h-4 mr-2" /> Request Resubmission
                    </Button>
                  </>
                )}
                {status === 'verified' && !booking.company_signature && (
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => updateStatus('confirmed')} data-testid="confirm-booking-btn">
                    <CheckCircle className="w-4 h-4 mr-2" /> Confirm Booking
                  </Button>
                )}
                {['submitted', 'verified', 'confirmed'].includes(status) && !booking.company_signature && (
                  <Button className="bg-[#D4AF37] text-[#0F172A] hover:bg-[#B5952F] hover:text-white font-bold" onClick={() => setSignOpen(true)} data-testid="company-sign-btn">
                    <PenLine className="w-4 h-4 mr-2" /> Sign &amp; Confirm
                  </Button>
                )}
                {booking.company_signature && (
                  <Button variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/15 hover:text-white" onClick={emailSignedCopy} disabled={emailing} data-testid="email-signed-copy-btn">
                    <Mail className="w-4 h-4 mr-2" /> {emailing ? 'Sending...' : 'Email Signed Copy'}
                  </Button>
                )}
                {['submitted', 'verified', 'confirmed'].includes(status) && (
                  <Button variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white/15 hover:text-white" onClick={() => downloadBookingPdf(bookingId)} data-testid="download-pdf-btn">
                    <Download className="w-4 h-4 mr-2" /> {booking.company_signature ? 'Download Signed Copy' : 'Download PDF'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
          <FillTypeLegend />
          <p className="text-xs text-slate-500">Office fields are always editable; every change is recorded in the activity log.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {sections.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-400">This template has no sections.</div>
            ) : sections.map((section, idx) => (
              <OfficeSectionCard key={section.section_id} section={section} index={idx} officeValues={officeValues}
                clientResponses={booking.client_responses || {}} onChange={onFieldChange} />
            ))}
            <DeclarationPanel declaration={schema.declaration} booking={booking} />
          </div>
          <aside className="space-y-6">
            <ClientLinkPanel booking={booking} />
            <DocumentsPanel uploads={schema.required_uploads} files={booking.files} />
            <TimelinePanel events={booking.events} />
          </aside>
        </div>
      </div>

      {dirty && (
        <div className="fixed bottom-4 inset-x-0 flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto bg-[#0F172A] text-white rounded-full shadow-2xl pl-5 pr-2 py-2 flex items-center gap-3 border border-[#D4AF37]/40" data-testid="unsaved-changes-bar">
            <span className="text-sm">You have unsaved office changes</span>
            <Button size="sm" onClick={saveOffice} disabled={saving} className="rounded-full bg-[#D4AF37] text-[#0F172A] hover:bg-[#B5952F] hover:text-white font-bold" data-testid="save-office-fields-floating-btn">
              {saving ? 'Saving...' : 'Save now'}
            </Button>
          </div>
        </div>
      )}

      <SendLinkDialog open={sendLinkOpen} onOpenChange={setSendLinkOpen} booking={booking} onSent={fetchBooking} />
      <CompanySignDialog open={signOpen} onOpenChange={setSignOpen} booking={booking} onSigned={fetchBooking} />
    </div>
  );
}
